import type { ChangeEvent, InputHTMLAttributes, JSX, Ref } from 'react'
import { forwardRef, useCallback, useEffect, useRef } from 'react'
import cc from 'classcat'

import { FormErrorMessage } from '@/components/ui/form-error-message'

import { useCombinedRefs } from '@/hooks'

import styles from './text-field.module.scss'

interface TextFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>, 'className'> {
  label?: string | JSX.Element
  error?: string
  classNameContainer?: string
  classNameContentWrapper?: string
  classNameError?: string
  classNameInput?: string
  rows?: number
  autoRows?: boolean
  icon?: JSX.Element
  rightPlaceholder?: string
  endAdornment?: React.ReactNode
  blank?: boolean
  variant?: 'primary' | 'secondary'
}

const TextField = forwardRef<HTMLInputElement | HTMLTextAreaElement, TextFieldProps>(
  function TextField(
    {
      label,
      error,
      classNameContainer,
      classNameContentWrapper,
      classNameError,
      classNameInput,
      rows,
      autoRows,
      icon,
      onChange,
      type,
      rightPlaceholder,
      endAdornment,
      blank,
      variant = 'primary',
      ...rest
    },
    ref,
  ) {
    const innerRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null)
    const combinedRef = useCombinedRefs(ref, innerRef)

    const autoResize = useCallback(() => {
      if (combinedRef.current) {
        combinedRef.current.style.height = `${combinedRef.current.scrollHeight}px`
      }
    }, [combinedRef])

    const handleChangeInput = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (autoRows) {
        autoResize()
      }

      onChange?.(e)
    }

    const textFieldProps = {
      ...rest,
      className: cc([
        styles.input_wrapper__content__textField,
        styles.text_field_input,
        classNameInput,
        {
          [styles.input_wrapper__content__textField_with_icon]: icon,
          [styles.input_wrapper__content__textField_textarea]: rows,
          [styles.input_wrapper__content__textField_textarea__auto]: autoRows,
        },
      ]),
      onChange: handleChangeInput,
    }

    useEffect(() => {
      autoResize()
    }, [autoResize])

    return (
      <div className={cc([styles.input_wrapper, classNameContainer])}>
        {Boolean(label) && <label className={styles.input_wrapper__label}>{label}</label>}

        <div
          className={cc([
            styles.input_wrapper__content,
            classNameContentWrapper,
            {
              [styles.input_wrapper__content__withError]: error,
              [styles.input_wrapper__content__blank]: blank,
              [styles.input_wrapper__content__secondary]: variant === 'secondary',
            },
          ])}
        >
          {Boolean(icon) && (
            <div className={styles.input_wrapper__content__icon_wrapper}>{icon}</div>
          )}

          <div className={styles.input_wrapper__content__input_container}>
            {rows ? (
              <textarea
                ref={combinedRef as Ref<HTMLTextAreaElement>}
                {...textFieldProps}
                rows={rows}
              />
            ) : (
              <input ref={combinedRef as Ref<HTMLInputElement>} {...textFieldProps} type={type} />
            )}
            {rightPlaceholder && (
              <div className={styles.input_wrapper__content__right_placeholder}>
                {rightPlaceholder}
              </div>
            )}
            {endAdornment && (
              <div className={styles.input_wrapper__content__end_adornment}>{endAdornment}</div>
            )}
          </div>
        </div>

        <FormErrorMessage error={error} className={classNameError} />
      </div>
    )
  },
)

export default TextField
